import React, { useContext, useEffect, useState } from 'react';

import { useStyles } from './styles';

import FormFirmHeader from '../../components/FormFirmHeader/FormFirmHeader';
import FormHeader from '../../components/FormHeader/FormHeader';
import FormRow from '../../components/FormRow/FormRow';
import FormFooter from '../../components/FormFooter/FormFooter';
import ClxConfirmDialog from '../../components/ClxConfirmDialog/ClxConfirmDialog';

import { DataContext } from '../../contexts/DataContext';
import { Button } from '@material-ui/core';
import FormSubmitted from '../FormSubmitted/FormSubmitted';

import { INTAKE_FORM_STATUS } from '../../services/constants';
import useIsFormInstancePreview from '../../hooks/useIsFormInstancePreview/useIsFormInstancePreview';

const FormContainer = React.memo(() => {
    const classes = useStyles();

    const {
        formTemplateData,
        formInstanceData,
        putFormChange,
        submitForm,
        putFormRequest,
        putSelectedCountry,
        countryList,
        selectedCountry,
        loadingCountry,
        posting,
    }: any = useContext(DataContext);

    const [formValidation, setFormValidation] = useState({});
    const [formValid, setFormValid] = useState({});
    const [confirmDialogMessage, setConfirmDialogMessage] = useState<string>(
        'Are you sure you want to submit this form?',
    );
    const [confirmDialogOpen, setConfirmDialogOpen] = useState<boolean>(false);
    const isFormInstance = !!formInstanceData.name;
    const isFormPreview = useIsFormInstancePreview();

    const data = isFormInstance ? formInstanceData : formTemplateData;

    const onValid = React.useCallback((item: string) => {
        setFormValidation((prevFormValidation) => {
            let newValidation = { ...prevFormValidation };
            newValidation[item] = true;
            return newValidation;
        });
    }, []);
    const onInvalid = React.useCallback(
        (item: string) => {
            console.log('Pre: values:', formValidation);
            if (!formValidation[item]) {
                setFormValidation((prevFormValidation) => {
                    let newValidation = { ...prevFormValidation };
                    newValidation[item] = false;
                    return newValidation;
                });
            }
        },
        [formValidation],
    );

    const isFormEmpty = (): boolean => {
        const { fields } = data;
        let isFormEmpty = true;
        for (let i = 0; i < fields.length; i++) {
            if (!!fields[i].value || fields[i].value === 0) {
                isFormEmpty = false;
            }
            break;
        }
        return isFormEmpty;
    };
    const validateMaxLength = (): boolean => {
        const { fields } = data;
        let validateMaxLength = true;
        for (let i = 0; i < fields.length; i++) {
            if (fields[i].value !== null) {
                if (fields[i].maxLength > 0 && fields[i].value.length > fields[i].maxLength) {
                    validateMaxLength = false;
                    break;
                }
            }
        }
        return validateMaxLength;
    };
    const handleSubmitForm = () => {
        const validLength = validateMaxLength();
        if (!validLength) {
            return;
        }
        const emptyForm = isFormEmpty();
        if (emptyForm) {
            setConfirmDialogMessage(
                'You have left all the fields blank. Are you sure you want to submit this form?',
            );
        }
        setConfirmDialogOpen(true);
    };
    const closeSubmitForm = React.useCallback(() => {
        setConfirmDialogOpen(false);
    }, []);
    const confirmSubmitForm = React.useCallback(() => {
        submitForm(data.formId);
        closeSubmitForm();
    }, [data.formId, closeSubmitForm, submitForm]);

    useEffect(() => {
        let formValid = true;
        for (let key in formValidation) {
            if (!formValidation[key]) {
                formValid = false;
                break;
            }
        }
        setFormValid(formValid);
    }, [formValidation]);

    return (
        <>
            {data.status === INTAKE_FORM_STATUS.SUBMITTED && !isFormPreview ? (
                <FormSubmitted />
            ) : (
                <div className={classes.formContainer}>
                    <FormFirmHeader
                        firmName={data.firmDisplayName}
                        formId={data.formId}
                        dueDate={data.dueDate}
                    />
                    <FormHeader name={data.name} description={data.description} key="formHeader" />
                    <form>
                        {data.fields?.map((field: any, i: any) => {
                            const id = field.id || field.fieldId;
                            return (
                                <FormRow
                                    rowData={field}
                                    isFormInstance={isFormInstance}
                                    isFormPreview={isFormPreview}
                                    putFormChange={putFormChange}
                                    formId={data.formId}
                                    formStatus={data.status}
                                    onValid={onValid}
                                    onInvalid={onInvalid}
                                    putFormRequest={putFormRequest}
                                    putSelectedCountry={putSelectedCountry}
                                    key={id}
                                    countryList={countryList}
                                    selectedCountry={selectedCountry}
                                    loadingCountry={loadingCountry}
                                />
                            );
                        })}
                        <div className={`${classes.alignRight} ${classes.buttonContainer}`}>
                            <Button
                                key="formButton"
                                variant="contained"
                                color="primary"
                                disabled={
                                    !formValid ||
                                    data.status === INTAKE_FORM_STATUS.SUBMITTED ||
                                    !isFormInstance ||
                                    isFormPreview ||
                                    posting ||
                                    loadingCountry ||
                                    !validateMaxLength()
                                }
                                onClick={handleSubmitForm}
                            >
                                Submit
                            </Button>
                        </div>
                    </form>
                    <FormFooter />
                </div>
            )}
            <ClxConfirmDialog
                yesCallback={confirmSubmitForm}
                noCallback={closeSubmitForm}
                message={confirmDialogMessage}
                noButtonLabel="No"
                open={confirmDialogOpen}
            />
        </>
    );
});

export default FormContainer;
